import { Button } from "@/ds/shadcn/components/ui/button";
import { Drawer, DrawerContent } from "@/ds/shadcn/components/ui/drawer";
import { cn } from "@/ds/utils";
import { HeightExpander } from "@/features/common/components/HeightExpander";
import { screenSizeIsConsideredDesktop, screenSizeIsConsideredMobile, screenSizeIsConsideredWideScreen, useScreenSize } from "@/lib/common/ScreenSizeContext";
import { PortalContainer } from "@/lib/common/usePortalContainer";
import { ArrowLeft, X } from "lucide-react";
import { createContext, useContext, useMemo, type FC, type PropsWithChildren, type RefObject } from "react";

interface SplitScreenContextParameters {
	fixed: boolean;
	visible: boolean;
	onVisibleChange: (visible: boolean) => void;
}

const splitScreenContext = createContext<SplitScreenContextParameters>({
	fixed: true,
	visible: false,
	onVisibleChange: () => { }
});

export interface SplitScreenProps extends PropsWithChildren {
	fixed?: boolean;
	visible: boolean;
	onVisibleChange: (visible: boolean) => void;
}

export const SplitScreen: FC<SplitScreenProps> = props => {
	const {
		children,
		fixed = false,
		visible,
		onVisibleChange
	} = props;

	const screenSize = useScreenSize();
	const splitScreenContextParameters: SplitScreenContextParameters = useMemo(() => ({
		fixed,
		visible,
		onVisibleChange
	}), [fixed, visible, onVisibleChange]);

	return (
		<splitScreenContext.Provider value={splitScreenContextParameters}>
			<HeightExpander fixed={fixed} className={cn("@lg:relative", screenSizeIsConsideredWideScreen(screenSize) && "flex")}>
				{children}
			</HeightExpander>
		</splitScreenContext.Provider>
	);
};

export interface SplitScreenLeftPanelProps extends PropsWithChildren {
	className?: string;
	ref?: RefObject<HTMLDivElement | null>
}

export const SplitScreenLeftPanel: FC<SplitScreenLeftPanelProps> = props => {
	const {
		children,
		className,
		ref
	} = props;

	const splitScreenContextParameters = useContext(splitScreenContext);
	const screenSize = useScreenSize();
	const panelClassName = cn(
		"@container",
		!screenSizeIsConsideredMobile(screenSize) && "h-full",
		screenSizeIsConsideredWideScreen(screenSize) && "basis-0 grow",
		splitScreenContextParameters.fixed && "overflow-y-auto",
		className
	);

	return (
		<div ref={ref} className={panelClassName}>
			{children}
		</div>
	);
};

export interface SplitScreenRightPanelProps extends PropsWithChildren {
	className?: string;
	ref?: RefObject<HTMLDivElement | null>;
	onBack?: () => void;
}

export const SplitScreenRightPanel: FC<SplitScreenRightPanelProps> = props => {
	const screenSize = useScreenSize();

	if (screenSizeIsConsideredWideScreen(screenSize)) {
		return (
			<SplitScreenRightPanelWideScreen {...props} />
		);
	}

	if (screenSizeIsConsideredDesktop(screenSize)) {
		return (
			<SplitScreenRightPanelDesktop {...props} />
		);
	}

	return (
		<SplitScreenRightPanelMobile {...props} />
	);
};

export const SplitScreenRightPanelWideScreen: FC<SplitScreenRightPanelProps> = props => {
	const {
		children,
		className,
		ref,
		onBack
	} = props;

	const {
		fixed,
		visible,
		onVisibleChange
	} = useContext(splitScreenContext);

	const panelClassName = cn(
		"@container",
		"h-full basis-0 border-l hidden",
		fixed && "overflow-y-auto",
		visible && "grow block",
		className
	);

	return (
		<div className={panelClassName} ref={ref}>
			<div className={cn("flex items-center p-3 border-b", onBack ? "justify-between" : "justify-end")}>
				{onBack && <Button variant="ghost" className="h-6 p-2 text-xs" onClick={onBack}><ArrowLeft />Back</Button>}
				<Button variant="ghost" size="icon-xs" aria-label="Close" onClick={() => onVisibleChange(false)}><X /></Button>
			</div>

			<div>
				{children}
			</div>
		</div>
	);
};

interface SplitScreenRightPanelDrawerProps extends SplitScreenRightPanelProps {
	direction?: "right";
}

const SplitScreenRightPanelDrawer: FC<SplitScreenRightPanelDrawerProps> = props => {
	const {
		children,
		className,
		ref,
		direction,
		onBack
	} = props;

	const {
		visible,
		onVisibleChange
	} = useContext(splitScreenContext);

	const panelClassName = cn("h-full no-scrollbar overflow-y-auto", className);

	return (
		<Drawer open={visible} direction={direction} onOpenChange={onVisibleChange}>
			<DrawerContent>
				<PortalContainer className={panelClassName} ref={ref}>
					{
						onBack &&
						<div className="p-3 border-b">
							<Button variant="ghost" className="h-6 px-2 text-xs" onClick={onBack}><ArrowLeft />Back</Button>
						</div>
					}

					{children}
				</PortalContainer>
			</DrawerContent>
		</Drawer>
	);
};

export const SplitScreenRightPanelDesktop: FC<SplitScreenRightPanelProps> = props => (
	<SplitScreenRightPanelDrawer direction="right" {...props} />
);

export const SplitScreenRightPanelMobile: FC<SplitScreenRightPanelProps> = props => (
	<SplitScreenRightPanelDrawer {...props} />
);
